import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { useToast } from '../hooks/use-toast';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send message');
      }

      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: "Failed to Send",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-16 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get in Touch with Wanderloop</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Have a question, feedback, or want to share your walking experience? We'd love to hear from you.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-gray-600">Use the form to reach us</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium">Response Time</p>
                    <p className="text-gray-600">Within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle>Quick Questions?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/about" className="block text-amber-600 hover:text-amber-800 transition-colors">
                  → About Wanderloop
                </Link>
                <Link to="/walks" className="block text-amber-600 hover:text-amber-800 transition-colors">
                  → Browse All Walks
                </Link>
                <Link to="/submit" className="block text-amber-600 hover:text-amber-800 transition-colors">
                  → Submit Your Walk
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 border-0">
              <CardContent className="p-6 text-center">
                <MessageCircle className="h-8 w-8 text-black mx-auto mb-3" />
                <h3 className="font-semibold text-black mb-2">Community Feedback</h3>
                <p className="text-sm text-black/70 mb-4">
                  Your insights help us improve Wanderloop for everyone
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Send us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Your Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Select value={formData.subject} onValueChange={(value) => handleInputChange('subject', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="What's this about?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Question</SelectItem>
                        <SelectItem value="feedback">Website Feedback</SelectItem>
                        <SelectItem value="walk-suggestion">Walk Suggestion</SelectItem>
                        <SelectItem value="walk-correction">Walk Information Correction</SelectItem>
                        <SelectItem value="partnership">Partnership Inquiry</SelectItem>
                        <SelectItem value="technical">Technical Issue</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">What's on your mind? *</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us what you're thinking about, any feedback, questions, or ideas you'd like to share..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={6}
                      required
                    />
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <p className="text-sm text-gray-500">
                      We'll get back to you within 24 hours
                    </p>
                    <Button 
                      type="submit" 
                      size="lg" 
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold px-8 py-3"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 text-amber-600">How do I submit my own walk?</h4>
                  <p className="text-gray-600 text-sm">
                    Visit our <Link to="/submit" className="text-amber-600 hover:text-amber-800">Submit Walk page</Link> and fill out the comprehensive form. We review all submissions within 24-48 hours.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-amber-600">Can I suggest corrections to existing walks?</h4>
                  <p className="text-gray-600 text-sm">
                    Absolutely! Use the contact form above with "Walk Information Correction" as the subject and let us know what needs updating.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-amber-600">Do you have walks outside of Los Angeles?</h4>
                  <p className="text-gray-600 text-sm">
                    Wanderloop is built to grow with its community. Any city works — if you submit a walk from your city, it goes live immediately.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-amber-600">How do you choose which walks to feature?</h4>
                  <p className="text-gray-600 text-sm">
                    We curate walks based on scenic value, cultural interest, accessibility, and community feedback. Each walk undergoes our quality review process.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Floating Actions */}
      <FloatingActions />
      
      <Footer />
    </div>
  );
};

export default ContactPage;